import type { CSSProperties } from "react"
import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import ExpandableSection from "@cloudscape-design/components/expandable-section"
import Icon from "@cloudscape-design/components/icon"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import {
  colorBackgroundStatusError,
  colorBackgroundStatusSuccess,
  colorBackgroundStatusWarning,
  colorBorderDividerDefault,
  colorBorderDividerSecondary,
  colorTextBodySecondary,
} from "@cloudscape-design/design-tokens"

function stableStringify(obj: unknown): string {
  if (obj === null || obj === undefined) return JSON.stringify(obj)
  if (typeof obj !== "object") return JSON.stringify(obj)
  if (Array.isArray(obj)) return "[" + obj.map(stableStringify).join(",") + "]"
  const sorted = Object.keys(obj as Record<string, unknown>).sort()
  return (
    "{" +
    sorted
      .map((k) => JSON.stringify(k) + ":" + stableStringify((obj as Record<string, unknown>)[k]))
      .join(",") +
    "}"
  )
}

export type DocContent = Record<string, unknown>

interface Variable {
  order?: string | number
  variable_name?: string
  variable_label?: string
  format?: string
  max_length?: string
  description?: string
  unique_identifier?: boolean | string
  file_key?: boolean | string
  populate_for_all?: boolean | string
  codelist_ref?: string
}

interface CodelistItem {
  id?: string
  name?: string
  column?: string
  values?: { code?: string; decode?: string }[]
}

function isEmpty(val: unknown): boolean {
  if (val == null) return true
  if (Array.isArray(val) && val.length === 0) return true
  if (typeof val === "object" && Object.keys(val).length === 0) return true
  return false
}

function isIdentical(a: unknown, b: unknown): boolean {
  if (isEmpty(a) && isEmpty(b)) return true
  return stableStringify(a) === stableStringify(b)
}

function getSectionsWithChanges(from: DocContent, to: DocContent) {
  const ALL_SECTIONS = [
    { key: "ingestion", label: "Data Ingestion Parameters", field: "transfer_metadata" },
    { key: "schema", label: "Metadata", field: "transfer_metadata_schema" },
    { key: "test_concepts", label: "Test Concepts", field: "test_concepts" },
    { key: "dq", label: "Data Quality Rules", field: "data_quality_rules" },
    { key: "codelists", label: "Codelists", field: "code_lists" },
  ] as const

  type Section = {
    key: string
    label: string
    identical: boolean
    changeCount: number
    render: () => JSX.Element
  }

  const countChanges: Record<string, (from: DocContent, to: DocContent) => number> = {
    schema: (f, t) => {
      const fv = (f.transfer_metadata_schema as { variables?: Variable[] })?.variables || []
      const tv = (t.transfer_metadata_schema as { variables?: Variable[] })?.variables || []
      const fromNames = new Set(fv.map((v) => v.variable_name).filter(Boolean))
      const toNames = new Set(tv.map((v) => v.variable_name).filter(Boolean))
      let count = 0
      for (const n of toNames) if (!fromNames.has(n)) count++
      for (const n of fromNames) if (!toNames.has(n)) count++
      for (const n of fromNames)
        if (toNames.has(n)) {
          const a = fv.find((v) => v.variable_name === n)
          const b = tv.find((v) => v.variable_name === n)
          if (stableStringify(a) !== stableStringify(b)) count++
        }
      return count
    },
    codelists: (f, t) => {
      const fl = (f.code_lists as CodelistItem[]) || []
      const tl = (t.code_lists as CodelistItem[]) || []
      const fromNames = new Set(fl.map((c) => c.name || c.id).filter(Boolean))
      const toNames = new Set(tl.map((c) => c.name || c.id).filter(Boolean))
      let count = 0
      for (const n of toNames) if (!fromNames.has(n)) count++
      for (const n of fromNames) if (!toNames.has(n)) count++
      for (const n of fromNames)
        if (toNames.has(n)) {
          const a = fl.find((c) => (c.name || c.id) === n)
          const b = tl.find((c) => (c.name || c.id) === n)
          if (stableStringify(a) !== stableStringify(b)) count++
        }
      return count
    },
    dq: (f, t) => {
      const fm =
        ((f.data_quality_rules as DocContent)?.majorRules as {
          type?: string
          column?: string
        }[]) || []
      const tm =
        ((t.data_quality_rules as DocContent)?.majorRules as {
          type?: string
          column?: string
        }[]) || []
      const fmi =
        ((f.data_quality_rules as DocContent)?.minorRules as {
          type?: string
          column?: string
        }[]) || []
      const tmi =
        ((t.data_quality_rules as DocContent)?.minorRules as {
          type?: string
          column?: string
        }[]) || []
      const toKey = (r: { type?: string; column?: string }) => `${r.type}:${r.column}`
      const oldMap = new Map([...fm, ...fmi].map((r) => [toKey(r), r]))
      const newMap = new Map([...tm, ...tmi].map((r) => [toKey(r), r]))
      let count = 0
      for (const k of newMap.keys()) if (!oldMap.has(k)) count++
      for (const k of oldMap.keys()) if (!newMap.has(k)) count++
      for (const k of oldMap.keys())
        if (newMap.has(k) && stableStringify(oldMap.get(k)) !== stableStringify(newMap.get(k)))
          count++
      return count
    },
    test_concepts: (f, t) => {
      const fl = (f.test_concepts as DocContent[]) || []
      const tl = (t.test_concepts as DocContent[]) || []
      const key = (tc: DocContent) => (tc.id || tc.name) as string
      const fromIds = new Set(fl.map(key).filter(Boolean))
      const toIds = new Set(tl.map(key).filter(Boolean))
      let count = 0
      for (const id of toIds) if (!fromIds.has(id)) count++
      for (const id of fromIds) if (!toIds.has(id)) count++
      for (const id of fromIds)
        if (toIds.has(id)) {
          const a = fl.find((tc) => key(tc) === id)
          const b = tl.find((tc) => key(tc) === id)
          if (stableStringify(a) !== stableStringify(b)) count++
        }
      return count
    },
    ingestion: (f, t) => {
      const a = stableStringify(f.transfer_metadata || {})
      const b = stableStringify(t.transfer_metadata || {})
      return a === b ? 0 : 1
    },
  }

  const renderers: Record<string, (from: DocContent, to: DocContent) => JSX.Element> = {
    schema: (f, t) => (
      <SchemaDiff
        fromVars={(f.transfer_metadata_schema as { variables?: Variable[] })?.variables || []}
        toVars={(t.transfer_metadata_schema as { variables?: Variable[] })?.variables || []}
      />
    ),
    codelists: (f, t) => (
      <CodelistDiff
        from={(f.code_lists as CodelistItem[]) || []}
        to={(t.code_lists as CodelistItem[]) || []}
      />
    ),
    dq: (f, t) => (
      <DQRulesDiff
        from={f.data_quality_rules as DocContent}
        to={t.data_quality_rules as DocContent}
      />
    ),
    test_concepts: (f, t) => (
      <TestConceptsDiff
        from={(f.test_concepts as DocContent[]) || []}
        to={(t.test_concepts as DocContent[]) || []}
      />
    ),
    ingestion: (f, t) => (
      <FieldDiff from={f.transfer_metadata as DocContent} to={t.transfer_metadata as DocContent} />
    ),
  }

  return ALL_SECTIONS.map(
    (s): Section => ({
      key: s.key,
      label: s.label,
      identical: isIdentical(from[s.field], to[s.field]),
      changeCount: isIdentical(from[s.field], to[s.field]) ? 0 : countChanges[s.key](from, to),
      render: () => renderers[s.key](from, to),
    }),
  )
}

type DiffKind = "added" | "removed" | "modified"

const DIFF_KIND_LABEL: Record<DiffKind, string> = {
  added: "Added",
  removed: "Removed",
  modified: "Changed",
}

const DIFF_KIND_COLOR: Record<DiffKind, "green" | "red" | "severity-medium"> = {
  added: "green",
  removed: "red",
  modified: "severity-medium",
}

function diffKind(hasFrom: boolean, hasTo: boolean): DiffKind {
  if (!hasFrom) return "added"
  if (!hasTo) return "removed"
  return "modified"
}

const sx: Record<string, CSSProperties> = {
  list: { display: "flex", flexDirection: "column", gap: "8px" },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "3px",
    border: `1px solid ${colorBorderDividerDefault}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  side: { padding: "8px 12px", fontSize: "13px", minHeight: "2rem" },
  emptyCell: { display: "block", height: "100%" },
  changed: { fontWeight: 600, background: colorBackgroundStatusWarning },
  changedValue: { background: colorBackgroundStatusWarning },
  values: { marginTop: "4px", fontSize: "12px", color: colorTextBodySecondary },
  pattern: {
    display: "block",
    fontFamily: "monospace",
    fontSize: "12px",
    color: colorTextBodySecondary,
    marginTop: "2px",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "max-content", minWidth: "100%", borderCollapse: "collapse", fontSize: "12px" },
  th: {
    textAlign: "left",
    padding: "4px 8px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    color: colorTextBodySecondary,
    borderBottom: `1px solid ${colorBorderDividerDefault}`,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "4px 8px",
    borderBottom: `1px solid ${colorBorderDividerDefault}`,
    whiteSpace: "nowrap",
  },
  more: {
    textAlign: "center",
    fontStyle: "italic",
    color: colorTextBodySecondary,
    fontSize: "12px",
  },
}

// Status badge shown on the side that carries the change (right for additions,
// left for removals, both for modifications), so add/change/remove is conveyed by
// a Cloudscape Badge rather than a background color.
function DiffStatus({ kind, side }: { kind: DiffKind; side: "left" | "right" }) {
  const onColoredSide = side === "left" ? kind !== "added" : kind !== "removed"
  if (!onColoredSide) return null
  return (
    <span style={{ flexShrink: 0 }}>
      <Badge color={DIFF_KIND_COLOR[kind]}>{DIFF_KIND_LABEL[kind]}</Badge>
    </span>
  )
}

// Hatch pattern marks the side that has no value (added -> left empty, removed -> right empty).
const HATCH = `repeating-linear-gradient(-45deg, transparent, transparent 4px, ${colorBorderDividerSecondary} 4px, ${colorBorderDividerSecondary} 8px)`

const KIND_BG: Record<DiffKind, { left: string; right: string }> = {
  added: { left: HATCH, right: colorBackgroundStatusSuccess },
  removed: { left: colorBackgroundStatusError, right: HATCH },
  modified: { left: colorBackgroundStatusWarning, right: colorBackgroundStatusWarning },
}

// A two-column (from | to) grid row that keeps a `data-diff-item` anchor for reference scroll/flash.
function DiffRow({
  itemKey,
  kind,
  left,
  right,
}: {
  itemKey: string
  kind: DiffKind
  left: JSX.Element
  right: JSX.Element
}) {
  return (
    <div data-diff-item={itemKey} style={sx.row}>
      <div style={{ ...sx.side, background: KIND_BG[kind].left }}>{left}</div>
      <div style={{ ...sx.side, background: KIND_BG[kind].right }}>{right}</div>
    </div>
  )
}

function changedStyle(changed: boolean): CSSProperties | undefined {
  return changed ? sx.changed : undefined
}

const SCHEMA_HEADERS = [
  "Status",
  "Variable",
  "Label",
  "Fmt",
  "Len",
  "Description",
  "Codelist",
  "UID",
  "Key",
  "Pop",
]

function SchemaDiff({ fromVars, toVars }: { fromVars: Variable[]; toVars: Variable[] }) {
  const fromMap = new Map(fromVars.map((v) => [v.variable_name, v]))
  const toMap = new Map(toVars.map((v) => [v.variable_name, v]))
  const allNames = [
    ...new Set([...fromVars.map((v) => v.variable_name), ...toVars.map((v) => v.variable_name)]),
  ]

  const rows = allNames.filter((name) => {
    const from = fromMap.get(name)
    const to = toMap.get(name)
    return !(from && to && stableStringify(from) === stableStringify(to))
  })

  const renderCells = (
    v: Variable | undefined,
    side: "left" | "right",
    kind: DiffKind,
    other?: Variable,
  ) => {
    if (!v) return SCHEMA_HEADERS.map((_, i) => <td key={i} style={sx.td} />)
    const changed = (field: keyof Variable) => Boolean(other && v[field] !== other[field])
    const cell = (key: string, content: React.ReactNode, isChanged: boolean) => (
      <td key={key} style={{ ...sx.td, ...(isChanged ? sx.changed : undefined) }}>
        {content}
      </td>
    )
    return [
      <td key="s" style={sx.td}>
        <DiffStatus kind={kind} side={side} />
      </td>,
      <td key="n" style={{ ...sx.td, fontWeight: 600 }}>
        {v.variable_name ?? ""}
      </td>,
      cell("l", v.variable_label ?? "", changed("variable_label")),
      cell("f", v.format ?? "", changed("format")),
      cell("m", v.max_length ?? "", changed("max_length")),
      cell("d", v.description ?? "", changed("description")),
      cell("c", v.codelist_ref ?? "", changed("codelist_ref")),
      cell(
        "u",
        v.unique_identifier ? <Icon name="check" size="small" /> : "",
        changed("unique_identifier"),
      ),
      cell("k", v.file_key ? <Icon name="check" size="small" /> : "", changed("file_key")),
      <td key="p" style={sx.td}>
        {v.populate_for_all ? <Icon name="check" size="small" /> : ""}
      </td>,
    ]
  }

  const renderTable = (side: "left" | "right") => (
    <div style={sx.tableWrap}>
      <table style={sx.table}>
        <thead>
          <tr>
            {SCHEMA_HEADERS.map((h) => (
              <th key={h} style={sx.th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((name) => {
            const kind = diffKind(!!fromMap.get(name), !!toMap.get(name))
            const v = side === "left" ? fromMap.get(name) : toMap.get(name)
            const other = side === "left" ? toMap.get(name) : fromMap.get(name)
            return (
              <tr
                key={name}
                data-diff-item={name}
                style={{ height: "32px", background: KIND_BG[kind][side] }}
              >
                {renderCells(v, side, kind, other)}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 3px 1fr" }}>
      {renderTable("left")}
      <div style={{ background: colorBorderDividerDefault }} />
      {renderTable("right")}
    </div>
  )
}

function CodelistDiff({ from, to }: { from: CodelistItem[]; to: CodelistItem[] }) {
  const fromMap = new Map(from.map((c) => [c.name || c.id, c]))
  const toMap = new Map(to.map((c) => [c.name || c.id, c]))
  const allNames = [
    ...new Set([...from.map((c) => c.name || c.id), ...to.map((c) => c.name || c.id)]),
  ]

  const renderCell = (
    c: CodelistItem | undefined,
    side: "left" | "right",
    kind: DiffKind,
    other?: CodelistItem,
  ) => {
    if (!c) return <span style={sx.emptyCell} />
    const valuesChanged = Boolean(
      other && JSON.stringify(c.values) !== JSON.stringify(other.values),
    )
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <DiffStatus kind={kind} side={side} />
          <span>
            <strong>{c.name}</strong> → {c.column}
          </span>
        </div>
        {c.values && (
          <div style={{ ...sx.values, ...changedStyle(valuesChanged) }}>
            {c.values.map((v) => v.code).join(", ")}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={sx.list}>
      {allNames.map((name) => {
        const f = fromMap.get(name)
        const t = toMap.get(name)
        if (f && t && stableStringify(f) === stableStringify(t)) return null
        const kind = diffKind(!!f, !!t)
        return (
          <DiffRow
            key={name}
            itemKey={name ?? ""}
            kind={kind}
            left={renderCell(f, "left", kind, t)}
            right={renderCell(t, "right", kind, f)}
          />
        )
      })}
    </div>
  )
}

function DQRulesDiff({ from, to }: { from?: DocContent; to?: DocContent }) {
  const fromMajor = (from?.majorRules || []) as {
    type?: string
    column?: string
    pattern?: string
  }[]
  const toMajor = (to?.majorRules || []) as { type?: string; column?: string; pattern?: string }[]
  const fromMinor = (from?.minorRules || []) as {
    type?: string
    column?: string
    pattern?: string
  }[]
  const toMinor = (to?.minorRules || []) as { type?: string; column?: string; pattern?: string }[]

  const toKey = (r: { type?: string; column?: string; pattern?: string }) => `${r.type}:${r.column}`

  const diffGroup = (oldRules: typeof fromMajor, newRules: typeof toMajor) => {
    const oldMap = new Map(oldRules.map((r) => [toKey(r), r]))
    const newMap = new Map(newRules.map((r) => [toKey(r), r]))
    const allKeys = [...new Set([...oldRules.map(toKey), ...newRules.map(toKey)])]
    return allKeys
      .map((key) => ({ key, from: oldMap.get(key), to: newMap.get(key) }))
      .filter(({ from: f, to: t }) => stableStringify(f) !== stableStringify(t))
  }

  const majorDiffs = diffGroup(fromMajor, toMajor)
  const minorDiffs = diffGroup(fromMinor, toMinor)

  if (majorDiffs.length === 0 && minorDiffs.length === 0)
    return (
      <Box color="text-body-secondary">
        <i>No rule changes</i>
      </Box>
    )

  const renderCell = (
    r: (typeof fromMajor)[0] | undefined,
    side: "left" | "right",
    kind: DiffKind,
    other?: (typeof fromMajor)[0],
  ) => {
    if (!r) return <span style={sx.emptyCell} />
    const patternChanged = Boolean(other && r.pattern !== other.pattern)
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <DiffStatus kind={kind} side={side} />
          <span>
            <strong>{r.type}</strong> on <em>{r.column}</em>
          </span>
        </div>
        {r.pattern ? (
          <span style={{ ...sx.pattern, ...changedStyle(patternChanged) }}>
            pattern: {r.pattern}
          </span>
        ) : null}
      </div>
    )
  }

  const renderGroup = (
    label: string,
    diffs: { key: string; from?: (typeof fromMajor)[0]; to?: (typeof toMajor)[0] }[],
  ) => (
    <div>
      <Box variant="h5" color="text-body-secondary">
        {label}
      </Box>
      <div style={sx.list}>
        {diffs.map(({ key, from: f, to: t }) => {
          const kind = diffKind(!!f, !!t)
          return (
            <DiffRow
              key={key}
              itemKey={key}
              kind={kind}
              left={renderCell(f, "left", kind, t)}
              right={renderCell(t, "right", kind, f)}
            />
          )
        })}
      </div>
    </div>
  )

  return (
    <SpaceBetween size="s">
      {majorDiffs.length > 0 && renderGroup("Major Rules", majorDiffs)}
      {minorDiffs.length > 0 && renderGroup("Minor Rules", minorDiffs)}
    </SpaceBetween>
  )
}

function TestConceptsDiff({ from, to }: { from: DocContent[]; to: DocContent[] }) {
  const fromMap = new Map(from.map((tc) => [tc.id as string, tc]))
  const toMap = new Map(to.map((tc) => [tc.id as string, tc]))
  const allIds = [
    ...new Set([...from.map((tc) => tc.id as string), ...to.map((tc) => tc.id as string)]),
  ]

  const diffs = allIds
    .map((id) => ({ id, from: fromMap.get(id), to: toMap.get(id) }))
    .filter(({ from: f, to: t }) => stableStringify(f) !== stableStringify(t))

  if (diffs.length === 0) return <Box color="text-body-secondary">No changes</Box>

  const renderConcept = (tc: DocContent, other?: DocContent) => {
    const cols = [
      ...((tc.conditionColumns as string[]) || []),
      ...((tc.valueColumns as string[]) || []),
    ]
    const rows = (tc.rows as DocContent[]) || []
    const otherRows = other ? (other.rows as DocContent[]) || [] : []
    if (rows.length === 0) return <em>Empty</em>
    return (
      <div style={sx.tableWrap}>
        <table style={sx.table}>
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c} style={sx.th}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((row, i) => (
              <tr key={i}>
                {cols.map((c) => {
                  const otherVal = otherRows[i] ? otherRows[i][c] : undefined
                  const changed =
                    other && otherRows[i] && JSON.stringify(row[c]) !== JSON.stringify(otherVal)
                  return (
                    <td key={c} style={{ ...sx.td, ...changedStyle(Boolean(changed)) }}>
                      {JSON.stringify(row[c] ?? "")}
                    </td>
                  )
                })}
              </tr>
            ))}
            {rows.length > 10 && (
              <tr>
                <td colSpan={cols.length} style={sx.more}>
                  +{rows.length - 10} more rows
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div style={sx.list}>
      {diffs.map(({ id, from: f, to: t }) => {
        const kind = diffKind(!!f, !!t)
        return (
          <DiffRow
            key={id}
            itemKey={id}
            kind={kind}
            left={
              f ? (
                <>
                  <DiffStatus kind={kind} side="left" />
                  {renderConcept(f, t)}
                </>
              ) : (
                <span style={sx.emptyCell} />
              )
            }
            right={
              t ? (
                <>
                  <DiffStatus kind={kind} side="right" />
                  {renderConcept(t, f)}
                </>
              ) : (
                <span style={sx.emptyCell} />
              )
            }
          />
        )
      })}
    </div>
  )
}

function FieldDiff({ from, to }: { from?: DocContent; to?: DocContent }) {
  const allKeys = [...new Set([...Object.keys(from || {}), ...Object.keys(to || {})])]
  const diffs = allKeys.filter(
    (k) => stableStringify((from || {})[k]) !== stableStringify((to || {})[k]),
  )
  if (diffs.length === 0) return <Box color="text-body-secondary">No changes</Box>
  return (
    <div style={sx.list}>
      {diffs.map((key) => {
        const hasFrom = (from || {})[key] != null
        const hasTo = (to || {})[key] != null
        const kind = diffKind(hasFrom, hasTo)
        return (
          <DiffRow
            key={key}
            itemKey={key}
            kind={kind}
            left={
              hasFrom ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <DiffStatus kind={kind} side="left" />
                  <span>
                    <strong>{key}</strong>:{" "}
                    <span style={sx.changedValue}>{JSON.stringify((from || {})[key])}</span>
                  </span>
                </div>
              ) : (
                <span style={sx.emptyCell} />
              )
            }
            right={
              hasTo ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <DiffStatus kind={kind} side="right" />
                  <span>
                    <strong>{key}</strong>:{" "}
                    <span style={sx.changedValue}>{JSON.stringify((to || {})[key])}</span>
                  </span>
                </div>
              ) : (
                <span style={sx.emptyCell} />
              )
            }
          />
        )
      })}
    </div>
  )
}

export function CompareContent({
  loading,
  fromVer,
  toVer,
  fromContent,
  toContent,
  highlightSection,
}: {
  loading: boolean
  fromVer: string
  toVer: string
  fromContent: DocContent | null
  toContent: DocContent | null
  highlightSection?: string
  highlightItem?: string
}) {
  const sections = getSectionsWithChanges(fromContent || {}, toContent || {})
  const totalChanges = sections.reduce((sum, s) => sum + s.changeCount, 0)
  const changedSectionCount = sections.filter((s) => !s.identical).length

  if (loading) {
    return (
      <Box textAlign="center" color="text-status-inactive" padding="l">
        <Spinner /> Loading…
      </Box>
    )
  }
  if (!fromContent) return null

  return (
    <SpaceBetween size="m">
      <Box variant="p" color="text-body-secondary">
        {totalChanges} total change{totalChanges !== 1 ? "s" : ""} across {changedSectionCount}{" "}
        section{changedSectionCount !== 1 ? "s" : ""}
      </Box>
      <ColumnLayout columns={2}>
        <Box fontWeight="bold" color="text-status-error">
          From (v{fromVer})
        </Box>
        <Box fontWeight="bold" color="text-status-success">
          To (v{toVer})
        </Box>
      </ColumnLayout>
      {sections.map((section) => (
        <ExpandableSection
          key={section.key}
          variant="container"
          headerText={section.label}
          headerActions={
            section.identical ? (
              <Badge color="blue">Identical</Badge>
            ) : (
              <Badge color="severity-medium">
                {section.changeCount} change{section.changeCount !== 1 ? "s" : ""}
              </Badge>
            )
          }
          defaultExpanded={!section.identical || section.key === highlightSection}
        >
          {section.identical ? (
            <Box color="text-body-secondary">No changes in this section.</Box>
          ) : (
            section.render()
          )}
        </ExpandableSection>
      ))}
    </SpaceBetween>
  )
}
